
import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Copy, Download } from 'lucide-react';
import Editor from '@monaco-editor/react';

const ScriptEditor = ({ 
  scriptContent = '', 
  onChange, 
  type = 'pre-request',
  logs = [],
  onClearLogs,
  showConsole = false
}) => {
  const [localContent, setLocalContent] = useState(scriptContent);
  const [consoleHeight, setConsoleHeight] = useState(200); 
  const [isDragging, setIsDragging] = useState(false);
  const containerRef = useRef(null);
  const dragRef = useRef(null);
  const editorRef = useRef(null);
  const monacoRef = useRef(null);
  const completionProviderRef = useRef(null);


  useEffect(() => {
    setLocalContent(scriptContent);
  }, [scriptContent]);


  useEffect(() => {
    return () => {
 
      if (completionProviderRef.current && monacoRef.current) {
        try {
          completionProviderRef.current.dispose();
        } catch (error) {
          // Ignore disposal errors
        }
      }
      
  
      if (editorRef.current) {
        try {
          editorRef.current.dispose();
        } catch (error) {
          // Ignore disposal errors
        }
      }
    };
  }, []);

  // monaco edtor configuration
  const editorOptions = {
    selectOnLineNumbers: true,
    roundedSelection: false,
    readOnly: false,
    cursorStyle: 'line',
    automaticLayout: true,
    minimap: { enabled: false },
    scrollBeyondLastLine: false,
    fontSize: 13,

    fontFamily: 'apple',
    lineNumbers: 'on',
    wordWrap: 'on',
    folding: true,
    bracketPairColorization: { enabled: true },
    lineHeight: 20,
    padding: { top: 16, bottom: 16 },
    suggest: {
      showKeywords: true,
      showSnippets: true,
      showFunctions: true,
      showConstructors: true,
      showFields: true,
      showVariables: true,
      showClasses: true,
      showStructs: true,
      showInterfaces: true,
      showModules: true,
      showProperties: true,
      showEvents: true,
      showOperators: true,
      showUnits: true,
      showValues: true,
      showConstants: true,
      showEnums: true,
      showEnumMembers: true,
      showColors: true,
      showFiles: true,
      showReferences: true,
      showFolders: true,
      showTypeParameters: true,
      showUsers: true,
      showIssues: true,
      showWords: true
    },
    theme: 'oneDarkPro'
  };
  

  const handleEditorDidMount = (editor, monaco) => {

    monaco.editor.defineTheme('oneDarkPro', {
    base: 'vs-dark',
    inherit: true,
    rules: [
      { token: 'comment', foreground: '5c6370', fontStyle: 'italic' },
      { token: 'keyword', foreground: 'c678dd' },
      { token: 'number', foreground: 'd19a66' },
      { token: 'string', foreground: '98c379' },
      { token: 'identifier', foreground: 'e06c75' },
      { token: 'type', foreground: '56b6c2' },
      { token: 'operator', foreground: 'abb2bf' },
      { token: 'delimiter', foreground: 'abb2bf' }
    ],
    colors: {
      'editor.background': '#282c34',
      'editor.foreground': '#abb2bf',
      'editor.lineHighlightBackground': '#2c313c',
      'editor.selectionBackground': '#3e4451',
      'editorCursor.foreground': '#528bff',
      'editorLineNumber.foreground': '#495162',
      'editorLineNumber.activeForeground': '#abb2bf'
    }
  });
  monaco.editor.setTheme('oneDarkPro');

   
    editorRef.current = editor;
    monacoRef.current = monaco;
    
    // Dispose previous provider if it exists to prevent duplicate suggestions
    if (completionProviderRef.current) {
      completionProviderRef.current.dispose();
    }
    
    completionProviderRef.current = monaco.languages.registerCompletionItemProvider('javascript', {
      provideCompletionItems: (model, position) => {
        const suggestions = [
          // NG Variables
          {
            label: 'ng.variables.set',
            kind: monaco.languages.CompletionItemKind.Method,
            insertText: 'ng.variables.set("${1:key}", "${2:value}")',
            insertTextRules: monaco.languages.CompletionItemInsertTextRule.InsertAsSnippet,
            documentation: 'Set a variable value'
          },
          {
            label: 'ng.variables.get',
            kind: monaco.languages.CompletionItemKind.Method,
            insertText: 'ng.variables.get("${1:key}")',
            insertTextRules: monaco.languages.CompletionItemInsertTextRule.InsertAsSnippet,
            documentation: 'Get a variable value'
          },
          {
            label: 'ng.variables.has',
            kind: monaco.languages.CompletionItemKind.Method,
            insertText: 'ng.variables.has("${1:key}")',
            insertTextRules: monaco.languages.CompletionItemInsertTextRule.InsertAsSnippet,
            documentation: 'Check if variable exists'
          },
          {
            label: 'ng.variables.unset',
            kind: monaco.languages.CompletionItemKind.Method,
            insertText: 'ng.variables.unset("${1:key}")',
            insertTextRules: monaco.languages.CompletionItemInsertTextRule.InsertAsSnippet,
            documentation: 'Remove a variable'
          },
          {
            label: 'ng.variables.toObject',
            kind: monaco.languages.CompletionItemKind.Method,
            insertText: 'ng.variables.toObject()',
            insertTextRules: monaco.languages.CompletionItemInsertTextRule.InsertAsSnippet,
            documentation: 'Get all variables as object'
          },
          // NG Environment
          {
            label: 'ng.environment.set',
            kind: monaco.languages.CompletionItemKind.Method,
            insertText: 'ng.environment.set("${1:key}", "${2:value}")',
            insertTextRules: monaco.languages.CompletionItemInsertTextRule.InsertAsSnippet,
            documentation: 'Set an environment variable'
          },
          {
            label: 'ng.environment.get',
            kind: monaco.languages.CompletionItemKind.Method,
            insertText: 'ng.environment.get("${1:key}")',
            insertTextRules: monaco.languages.CompletionItemInsertTextRule.InsertAsSnippet,
            documentation: 'Get an environment variable'
          },
          {
            label: 'ng.environment.has',
            kind: monaco.languages.CompletionItemKind.Method,
            insertText: 'ng.environment.has("${1:key}")',
            insertTextRules: monaco.languages.CompletionItemInsertTextRule.InsertAsSnippet,
            documentation: 'Check if environment variable exists'
          },
          {
            label: 'ng.environment.unset',
            kind: monaco.languages.CompletionItemKind.Method,
            insertText: 'ng.environment.unset("${1:key}")',
            insertTextRules: monaco.languages.CompletionItemInsertTextRule.InsertAsSnippet,
            documentation: 'Remove an environment variable'
          },
          {
            label: 'ng.environment.toObject',
            kind: monaco.languages.CompletionItemKind.Method,
            insertText: 'ng.environment.toObject()',
            insertTextRules: monaco.languages.CompletionItemInsertTextRule.InsertAsSnippet,
            documentation: 'Get all environment variables as object'
          },
          // PM Request
          {
            label: 'ng.request.url',
            kind: monaco.languages.CompletionItemKind.Property,
            insertText: 'ng.request.url',
            documentation: 'Request URL'
          },
          {
            label: 'ng.request.method',
            kind: monaco.languages.CompletionItemKind.Property,
            insertText: 'ng.request.method',
            documentation: 'Request method'
          },
          {
            label: 'ng.request.headers',
            kind: monaco.languages.CompletionItemKind.Property,
            insertText: 'ng.request.headers',
            documentation: 'Request headers'
          },
          {
            label: 'ng.request.body',
            kind: monaco.languages.CompletionItemKind.Property,
            insertText: 'ng.request.body',
            documentation: 'Request body'
          },
          {
            label: 'ng.request.queryParams',
            kind: monaco.languages.CompletionItemKind.Property,
            insertText: 'ng.request.queryParams',
            documentation: 'Query parameters'
          },
          // PM Request (Enhanced Methods)
          {
            label: 'ng.request.url.addQueryParams',
            kind: monaco.languages.CompletionItemKind.Method,
            insertText: 'ng.request.url.addQueryParams({${1:key}: "${2:value}"})',
            insertTextRules: monaco.languages.CompletionItemInsertTextRule.InsertAsSnippet,
            documentation: 'Add query parameters to URL'
          },
          {
            label: 'ng.request.url.removeQueryParams',
            kind: monaco.languages.CompletionItemKind.Method,
            insertText: 'ng.request.url.removeQueryParams(["${1:param1}", "${2:param2}"])',
            insertTextRules: monaco.languages.CompletionItemInsertTextRule.InsertAsSnippet,
            documentation: 'Remove query parameters from URL'
          },
          {
            label: 'ng.request.url.addPathParams',
            kind: monaco.languages.CompletionItemKind.Method,
            insertText: 'ng.request.url.addPathParams({${1:id}: "${2:123}"})',
            insertTextRules: monaco.languages.CompletionItemInsertTextRule.InsertAsSnippet,
            documentation: 'Add path parameters to URL'
          },
          {
            label: 'ng.request.headers.add',
            kind: monaco.languages.CompletionItemKind.Method,
            insertText: 'ng.request.headers.add({key: "${1:Header-Name}", value: "${2:value}"})',
            insertTextRules: monaco.languages.CompletionItemInsertTextRule.InsertAsSnippet,
            documentation: 'Add header to request'
          },
          {
            label: 'ng.request.headers.remove',
            kind: monaco.languages.CompletionItemKind.Method,
            insertText: 'ng.request.headers.remove("${1:Header-Name}")',
            insertTextRules: monaco.languages.CompletionItemInsertTextRule.InsertAsSnippet,
            documentation: 'Remove header from request'
          },
          {
            label: 'ng.request.headers.replace',
            kind: monaco.languages.CompletionItemKind.Method,
            insertText: 'ng.request.headers.replace({key: "${1:Header-Name}", value: "${2:new-value}"})',
            insertTextRules: monaco.languages.CompletionItemInsertTextRule.InsertAsSnippet,
            documentation: 'Replace header in request'
          },
          {
            label: 'ng.request.headers.get',
            kind: monaco.languages.CompletionItemKind.Method,
            insertText: 'ng.request.headers.get("${1:Header-Name}")',
            insertTextRules: monaco.languages.CompletionItemInsertTextRule.InsertAsSnippet,
            documentation: 'Get header value from request'
          },
          {
            label: 'ng.request.headers.has',
            kind: monaco.languages.CompletionItemKind.Method,
            insertText: 'ng.request.headers.has("${1:Header-Name}")',
            insertTextRules: monaco.languages.CompletionItemInsertTextRule.InsertAsSnippet,
            documentation: 'Check if header exists in request'
          },
          {
            label: 'ng.request.headers.all',
            kind: monaco.languages.CompletionItemKind.Method,
            insertText: 'ng.request.headers.all()',
            documentation: 'Get all headers from request'
          },
          {
            label: 'ng.request.body.formdata.add',
            kind: monaco.languages.CompletionItemKind.Method,
            insertText: 'ng.request.body.formdata.add({key: "${1:field}", value: "${2:value}"})',
            insertTextRules: monaco.languages.CompletionItemInsertTextRule.InsertAsSnippet,
            documentation: 'Add form data field'
          },
          {
            label: 'ng.request.body.formdata.remove',
            kind: monaco.languages.CompletionItemKind.Method,
            insertText: 'ng.request.body.formdata.remove("${1:field}")',
            insertTextRules: monaco.languages.CompletionItemInsertTextRule.InsertAsSnippet,
            documentation: 'Remove form data field'
          },
          {
            label: 'ng.request.body.urlencoded.add',
            kind: monaco.languages.CompletionItemKind.Method,
            insertText: 'ng.request.body.urlencoded.add({key: "${1:field}", value: "${2:value}"})',
            insertTextRules: monaco.languages.CompletionItemInsertTextRule.InsertAsSnippet,
            documentation: 'Add URL encoded field'
          },
          {
            label: 'ng.request.body.urlencoded.remove',
            kind: monaco.languages.CompletionItemKind.Method,
            insertText: 'ng.request.body.urlencoded.remove("${1:field}")',
            insertTextRules: monaco.languages.CompletionItemInsertTextRule.InsertAsSnippet,
            documentation: 'Remove URL encoded field'
          },
          {
            label: 'ng.request.auth.basic',
            kind: monaco.languages.CompletionItemKind.Method,
            insertText: 'ng.request.auth.basic({username: "${1:user}", password: "${2:pass}"})',
            insertTextRules: monaco.languages.CompletionItemInsertTextRule.InsertAsSnippet,
            documentation: 'Set basic authentication'
          },
          {
            label: 'ng.request.auth.bearer',
            kind: monaco.languages.CompletionItemKind.Method,
            insertText: 'ng.request.auth.bearer("${1:token}")',
            insertTextRules: monaco.languages.CompletionItemInsertTextRule.InsertAsSnippet,
            documentation: 'Set bearer token authentication'
          },
          // NG Request Query Params Methods
          {
            label: 'ng.request.queryParams.add',
            kind: monaco.languages.CompletionItemKind.Method,
            insertText: 'ng.request.queryParams.add("${1:key}", "${2:value}")',
            insertTextRules: monaco.languages.CompletionItemInsertTextRule.InsertAsSnippet,
            documentation: 'Add query parameter'
          },
          {
            label: 'ng.request.queryParams.remove',
            kind: monaco.languages.CompletionItemKind.Method,
            insertText: 'ng.request.queryParams.remove("${1:key}")',
            insertTextRules: monaco.languages.CompletionItemInsertTextRule.InsertAsSnippet,
            documentation: 'Remove query parameter'
          },
          {
            label: 'ng.request.queryParams.get',
            kind: monaco.languages.CompletionItemKind.Method,
            insertText: 'ng.request.queryParams.get("${1:key}")',
            insertTextRules: monaco.languages.CompletionItemInsertTextRule.InsertAsSnippet,
            documentation: 'Get query parameter value'
          },
          {
            label: 'ng.request.queryParams.has',
            kind: monaco.languages.CompletionItemKind.Method,
            insertText: 'ng.request.queryParams.has("${1:key}")',
            insertTextRules: monaco.languages.CompletionItemInsertTextRule.InsertAsSnippet,
            documentation: 'Check if query parameter exists'
          },
          {
            label: 'ng.request.queryParams.all',
            kind: monaco.languages.CompletionItemKind.Method,
            insertText: 'ng.request.queryParams.all()',
            documentation: 'Get all query parameters'
          },
          // ng Response
          {
            label: 'ng.response.status',
            kind: monaco.languages.CompletionItemKind.Property,
            insertText: 'ng.response.status',
            documentation: 'Response status code'
          },
          {
            label: 'ng.response.statusText',
            kind: monaco.languages.CompletionItemKind.Property,
            insertText: 'ng.response.statusText',
            documentation: 'Response status text'
          },
          {
            label: 'ng.response.headers',
            kind: monaco.languages.CompletionItemKind.Property,
            insertText: 'ng.response.headers',
            documentation: 'Response headers'
          },
          {
            label: 'ng.response.body',
            kind: monaco.languages.CompletionItemKind.Property,
            insertText: 'ng.response.body',
            documentation: 'Response body'
          },
          {
            label: 'ng.response.json',
            kind: monaco.languages.CompletionItemKind.Method,
            insertText: 'ng.response.json()',
            documentation: 'Parse response as JSON'
          },
          // ng Response 
          {
            label: 'ng.response.to.have.status',
            kind: monaco.languages.CompletionItemKind.Method,
            insertText: 'ng.response.to.have.status(${1:200})',
            insertTextRules: monaco.languages.CompletionItemInsertTextRule.InsertAsSnippet,
            documentation: 'Assert response status code'
          },
          {
            label: 'ng.response.to.have.header',
            kind: monaco.languages.CompletionItemKind.Method,
            insertText: 'ng.response.to.have.header("${1:Content-Type}", /${2:application\\/json}/)',
            insertTextRules: monaco.languages.CompletionItemInsertTextRule.InsertAsSnippet,
            documentation: 'Assert response header value'
          },
          // ng Response
          {
            label: 'ng.response.code',
            kind: monaco.languages.CompletionItemKind.Property,
            insertText: 'ng.response.code',
            documentation: 'Response status code (same as status)'
          },
          {
            label: 'ng.response.responseTime',
            kind: monaco.languages.CompletionItemKind.Property,
            insertText: 'ng.response.responseTime',
            documentation: 'Response time in milliseconds'
          },
          {
            label: 'ng.response.responseSize',
            kind: monaco.languages.CompletionItemKind.Property,
            insertText: 'ng.response.responseSize',
            documentation: 'Response size in bytes'
          },
          {
            label: 'ng.response.headers.all',
            kind: monaco.languages.CompletionItemKind.Method,
            insertText: 'ng.response.headers.all()',
            documentation: 'Get all response headers'
          },
          {
            label: 'ng.response.headers.get',
            kind: monaco.languages.CompletionItemKind.Method,
            insertText: 'ng.response.headers.get("${1:Header-Name}")',
            insertTextRules: monaco.languages.CompletionItemInsertTextRule.InsertAsSnippet,
            documentation: 'Get specific response header'
          },
          {
            label: 'ng.response.headers.has',
            kind: monaco.languages.CompletionItemKind.Method,
            insertText: 'ng.response.headers.has("${1:Header-Name}")',
            insertTextRules: monaco.languages.CompletionItemInsertTextRule.InsertAsSnippet,
            documentation: 'Check if response header exists'
          },
          {
            label: 'ng.response.text',
            kind: monaco.languages.CompletionItemKind.Method,
            insertText: 'ng.response.text()',
            documentation: 'Get response body as text'
          },
          {
            label: 'ng.response.xml',
            kind: monaco.languages.CompletionItemKind.Method,
            insertText: 'ng.response.xml()',
            documentation: 'Get response body as XML'
          },
          {
            label: 'ng.response.html',
            kind: monaco.languages.CompletionItemKind.Method,
            insertText: 'ng.response.html()',
            documentation: 'Get response body as HTML'
          },
          {
            label: 'ng.response.cookies.all',
            kind: monaco.languages.CompletionItemKind.Method,
            insertText: 'ng.response.cookies.all()',
            documentation: 'Get all response cookies'
          },
          {
            label: 'ng.response.cookies.get',
            kind: monaco.languages.CompletionItemKind.Method,
            insertText: 'ng.response.cookies.get("${1:cookie-name}")',
            insertTextRules: monaco.languages.CompletionItemInsertTextRule.InsertAsSnippet,
            documentation: 'Get specific response cookie'
          },
          {
            label: 'ng.response.cookies.has',
            kind: monaco.languages.CompletionItemKind.Method,
            insertText: 'ng.response.cookies.has("${1:cookie-name}")',
            insertTextRules: monaco.languages.CompletionItemInsertTextRule.InsertAsSnippet,
            documentation: 'Check if response cookie exists'
          },
          {
            label: 'ng.response.cookies.jar',
            kind: monaco.languages.CompletionItemKind.Method,
            insertText: 'ng.response.cookies.jar()',
            documentation: 'Get cookie jar for response'
          },
          // ng Test
          {
            label: 'ng.test',
            kind: monaco.languages.CompletionItemKind.Method,
            insertText: 'ng.test("${1:test name}", function () {\n\t${2:// test code}\n});',
            insertTextRules: monaco.languages.CompletionItemInsertTextRule.InsertAsSnippet,
            documentation: 'Create a test'
          },
          // ng Expect
          {
            label: 'ng.expect',
            kind: monaco.languages.CompletionItemKind.Method,
            insertText: 'ng.expect(${1:value})',
            insertTextRules: monaco.languages.CompletionItemInsertTextRule.InsertAsSnippet,
            documentation: 'Create an assertion'
          },
          // Chai Assertion Methods
          {
            label: 'ng.expect().to.equal',
            kind: monaco.languages.CompletionItemKind.Method,
            insertText: 'ng.expect(${1:value}).to.equal(${2:expected})',
            insertTextRules: monaco.languages.CompletionItemInsertTextRule.InsertAsSnippet,
            documentation: 'Assert strict equality'
          },
          {
            label: 'ng.expect().to.eql',
            kind: monaco.languages.CompletionItemKind.Method,
            insertText: 'ng.expect(${1:value}).to.eql(${2:expected})',
            insertTextRules: monaco.languages.CompletionItemInsertTextRule.InsertAsSnippet,
            documentation: 'Assert deep equality'
          },
          {
            label: 'ng.expect().to.be.true',
            kind: monaco.languages.CompletionItemKind.Method,
            insertText: 'ng.expect(${1:value}).to.be.true',
            insertTextRules: monaco.languages.CompletionItemInsertTextRule.InsertAsSnippet,
            documentation: 'Assert value is true'
          },
          {
            label: 'ng.expect().to.be.false',
            kind: monaco.languages.CompletionItemKind.Method,
            insertText: 'ng.expect(${1:value}).to.be.false',
            insertTextRules: monaco.languages.CompletionItemInsertTextRule.InsertAsSnippet,
            documentation: 'Assert value is false'
          },
          {
            label: 'ng.expect().to.be.null',
            kind: monaco.languages.CompletionItemKind.Method,
            insertText: 'ng.expect(${1:value}).to.be.null',
            insertTextRules: monaco.languages.CompletionItemInsertTextRule.InsertAsSnippet,
            documentation: 'Assert value is null'
          },
          {
            label: 'ng.expect().to.be.undefined',
            kind: monaco.languages.CompletionItemKind.Method,
            insertText: 'ng.expect(${1:value}).to.be.undefined',
            insertTextRules: monaco.languages.CompletionItemInsertTextRule.InsertAsSnippet,
            documentation: 'Assert value is undefined'
          },
          {
            label: 'ng.expect().to.be.a',
            kind: monaco.languages.CompletionItemKind.Method,
            insertText: 'ng.expect(${1:value}).to.be.a("${2:type}")',
            insertTextRules: monaco.languages.CompletionItemInsertTextRule.InsertAsSnippet,
            documentation: 'Assert value is of specific type'
          },
          {
            label: 'ng.expect().to.be.an',
            kind: monaco.languages.CompletionItemKind.Method,
            insertText: 'ng.expect(${1:value}).to.be.an("${2:type}")',
            insertTextRules: monaco.languages.CompletionItemInsertTextRule.InsertAsSnippet,
            documentation: 'Assert value is of specific type (an)'
          },
          {
            label: 'ng.expect().to.be.above',
            kind: monaco.languages.CompletionItemKind.Method,
            insertText: 'ng.expect(${1:value}).to.be.above(${2:threshold})',
            insertTextRules: monaco.languages.CompletionItemInsertTextRule.InsertAsSnippet,
            documentation: 'Assert value is above threshold'
          },
          {
            label: 'ng.expect().to.be.below',
            kind: monaco.languages.CompletionItemKind.Method,
            insertText: 'ng.expect(${1:value}).to.be.below(${2:threshold})',
            insertTextRules: monaco.languages.CompletionItemInsertTextRule.InsertAsSnippet,
            documentation: 'Assert value is below threshold'
          },
          {
            label: 'ng.expect().to.have.property',
            kind: monaco.languages.CompletionItemKind.Method,
            insertText: 'ng.expect(${1:value}).to.have.property("${2:property}")',
            insertTextRules: monaco.languages.CompletionItemInsertTextRule.InsertAsSnippet,
            documentation: 'Assert object has property'
          },
          {
            label: 'ng.expect().to.have.nested.property',
            kind: monaco.languages.CompletionItemKind.Method,
            insertText: 'ng.expect(${1:value}).to.have.nested.property("${2:nested.property}")',
            insertTextRules: monaco.languages.CompletionItemInsertTextRule.InsertAsSnippet,
            documentation: 'Assert object has nested property'
          },
          {
            label: 'ng.expect().to.include',
            kind: monaco.languages.CompletionItemKind.Method,
            insertText: 'ng.expect(${1:value}).to.include(${2:item})',
            insertTextRules: monaco.languages.CompletionItemInsertTextRule.InsertAsSnippet,
            documentation: 'Assert value includes item'
          },
          {
            label: 'ng.expect().to.include.members',
            kind: monaco.languages.CompletionItemKind.Method,
            insertText: 'ng.expect(${1:array}).to.include.members([${2:members}])',
            insertTextRules: monaco.languages.CompletionItemInsertTextRule.InsertAsSnippet,
            documentation: 'Assert array includes members'
          },
          {
            label: 'ng.expect().to.have.lengthOf',
            kind: monaco.languages.CompletionItemKind.Method,
            insertText: 'ng.expect(${1:array}).to.have.lengthOf(${2:length})',
            insertTextRules: monaco.languages.CompletionItemInsertTextRule.InsertAsSnippet,
            documentation: 'Assert array has specific length'
          },
          {
            label: 'ng.expect().to.match',
            kind: monaco.languages.CompletionItemKind.Method,
            insertText: 'ng.expect(${1:string}).to.match(/${2:regex}/)',
            insertTextRules: monaco.languages.CompletionItemInsertTextRule.InsertAsSnippet,
            documentation: 'Assert string matches regex'
          },
          {
            label: 'ng.expect().to.have.all.keys',
            kind: monaco.languages.CompletionItemKind.Method,
            insertText: 'ng.expect(${1:object}).to.have.all.keys("${2:key1}", "${3:key2}")',
            insertTextRules: monaco.languages.CompletionItemInsertTextRule.InsertAsSnippet,
            documentation: 'Assert object has all specified keys'
          },
          {
            label: 'ng.expect().to.not.be.empty',
            kind: monaco.languages.CompletionItemKind.Method,
            insertText: 'ng.expect(${1:value}).to.not.be.empty',
            insertTextRules: monaco.languages.CompletionItemInsertTextRule.InsertAsSnippet,
            documentation: 'Assert value is not empty'
          },
          {
            label: 'ng.expect().to.be.empty',
            kind: monaco.languages.CompletionItemKind.Method,
            insertText: 'ng.expect(${1:value}).to.be.empty',
            insertTextRules: monaco.languages.CompletionItemInsertTextRule.InsertAsSnippet,
            documentation: 'Assert value is empty'
          },
          // NG Console
          {
            label: 'ng.console.log',
            kind: monaco.languages.CompletionItemKind.Method,
            insertText: 'ng.console.log(${1:message})',
            insertTextRules: monaco.languages.CompletionItemInsertTextRule.InsertAsSnippet,
            documentation: 'Log to console'
          },
          {
            label: 'ng.console.error',
            kind: monaco.languages.CompletionItemKind.Method,
            insertText: 'ng.console.error(${1:message})',
            insertTextRules: monaco.languages.CompletionItemInsertTextRule.InsertAsSnippet,
            documentation: 'Log error to console'
          },
          {
            label: 'ng.console.warn',
            kind: monaco.languages.CompletionItemKind.Method,
            insertText: 'ng.console.warn(${1:message})',
            insertTextRules: monaco.languages.CompletionItemInsertTextRule.InsertAsSnippet,
            documentation: 'Log warning to console'
          },
          {
            label: 'ng.console.info',
            kind: monaco.languages.CompletionItemKind.Method,
            insertText: 'ng.console.info(${1:message})',
            insertTextRules: monaco.languages.CompletionItemInsertTextRule.InsertAsSnippet,
            documentation: 'Log info to console'
          }
        ];

        return { suggestions };
      }
    });
  };

  // resize logic
  const handleMouseDown = useCallback((e) => {
    e.preventDefault();
    setIsDragging(true);
    document.body.style.cursor = 'row-resize';
    document.body.style.userSelect = 'none';
  }, []);

  const handleMouseMove = useCallback((e) => {
    if (!isDragging || !containerRef.current) return;
    
    const containerRect = containerRef.current.getBoundingClientRect();
    const newHeight = containerRect.bottom - e.clientY;
    const minHeight = 100;
    const maxHeight = containerRect.height - 100;
    
    if (newHeight >= minHeight && newHeight <= maxHeight) {
      setConsoleHeight(newHeight);
    }
  }, [isDragging]);

  const handleMouseUp = useCallback(() => {
    setIsDragging(false);
    document.body.style.cursor = '';
    document.body.style.userSelect = '';
  }, []);


  useEffect(() => {
    if (isDragging) {
      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
    }

    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };
  }, [isDragging, handleMouseMove, handleMouseUp]);

  const handleContentChange = (value) => {
    setLocalContent(value);
    if (onChange) {
      onChange(value);
    }
  };

  const handleCopy = () => {
    navigator.clipboard.writeText(localContent);
  };

  const handleDownload = () => {
    const blob = new Blob([localContent], { type: 'text/javascript' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${type}-script.js`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };


  const getScriptTemplates = () => {
    if (type === 'pre-request') {
      return [
        {
          name: 'Set Variable',
          code: `// Set a variable
ng.variables.set("timestamp", Date.now());
ng.console.log("Timestamp set:", ng.variables.get("timestamp"));`
        },
        {
          name: 'Generate Random ID',
          code: `// Generate random ID
const randomId = Math.random().toString(36).substring(2, 15);
ng.variables.set("randomId", randomId);
ng.console.log("Random ID generated:", randomId);`
        },
        {
          name: 'Set Environment Variable',
          code: `// Set environment variable for current environment
ng.environment.set("api_key", "your-api-key-here");
ng.environment.set("base_url", "https://api.example.com");
ng.environment.set("timestamp", Date.now().toString());
ng.console.log("Environment variables set:", ng.environment.toObject());`
        },
        {
          name: 'Use Environment Variable',
          code: `// Use environment variable in request
const apiKey = ng.environment.get("x-api-key");
if (apiKey) {
  ng.console.log("API Key found:", apiKey);
} else {
  ng.console.log("No API key found in environment");
}

const baseUrl = ng.environment.get("base_url");
if (baseUrl) {
  ng.console.log("Base URL found:", baseUrl);
}`
        },
        {
          name: 'Set Request Body',
          code: `// Set request body with JSON data
let payload = {
    "name": "Firdous",
    "role": "Developer"
};

ng.request.setBody(JSON.stringify(payload));
ng.console.log("Request body set:", payload);`
        }
      ];
    } else {
      return [
        {
          name: 'Extract Data',
          code: `// Extract data from response
const responseData = ng.response.json();
ng.console.log("Full response data:", responseData);

if (responseData.id) {
  ng.variables.set("userId", responseData.id);
  ng.console.log("Extracted user ID:", ng.variables.get("userId"));
} else {
  ng.console.log("No ID found in response");
}`
        },
        {
          name: 'Debug Response',
          code: `// Debug response structure
ng.console.log("Response status:", ng.response.status);
ng.console.log("Response body:", ng.response.body);
ng.console.log("Response JSON:", ng.response.json());

// Check if response has testResults
const responseData = ng.response.json();
if (responseData.testResults) {
  ng.console.log("Test results found:", responseData.testResults);
}

// Check if response has originalResponse
if (responseData.originalResponse) {
  ng.console.log("Original response:", responseData.originalResponse);
}`
        },
        {
          name: 'Chai Assertions',
          code: `// Comprehensive Chai assertions
ng.test("Response status is 200", function () {
    ng.expect(ng.response.status).to.equal(200);
});

ng.test("Response has required fields", function () {
    const responseData = ng.response.json();
    ng.expect(responseData).to.have.property("id");
    ng.expect(responseData).to.have.property("name");
});

ng.test("Response data types are correct", function () {
    const responseData = ng.response.json();
    ng.expect(responseData.id).to.be.a("string");
    ng.expect(responseData.age).to.be.a("number");
    ng.expect(responseData.isActive).to.be.a("boolean");
});

ng.test("Response is not empty", function () {
    const responseData = ng.response.json();
    ng.expect(responseData).to.not.be.empty;
});

ng.test("Response time is acceptable", function () {
    ng.expect(ng.response.responseTime).to.be.below(2000);
});`
        },
        {
          name: 'Advanced Chai Tests',
          code: `// Advanced Chai assertions
ng.test("Deep equality check", function () {
    const responseData = ng.response.json();
    const expected = { id: "123", name: "John", active: true };
    ng.expect(responseData).to.deep.include(expected);
});

ng.test("Array validation", function () {
    const responseData = ng.response.json();
    ng.expect(responseData.items).to.be.an("array");
    ng.expect(responseData.items).to.have.lengthOf(3);
    ng.expect(responseData.items).to.include.members(["item1", "item2"]);
});

ng.test("String validation", function () {
    const responseData = ng.response.json();
    ng.expect(responseData.message).to.match(/success/i);
    ng.expect(responseData.email).to.match(/^[^@]+@[^@]+\\.[^@]+$/);
});

ng.test("Object structure validation", function () {
    const responseData = ng.response.json();
    ng.expect(responseData).to.have.all.keys("id", "name", "email", "createdAt");
    ng.expect(responseData).to.have.nested.property("user.profile.name");
});`
        }
      ];
    }
  };

  const insertTemplate = (template) => {
    const newContent = localContent + (localContent ? '\n\n' : '') + template.code;
    setLocalContent(newContent);
    if (onChange) {
      onChange(newContent);
    }
  };

  return (
    <div ref={containerRef} className="h-full flex flex-col bg-gray-900">
      <div className="flex items-center justify-between px-4 py-3 border-b border-gray-700 bg-gray-800">
        <div className="flex items-center gap-3">
          <h3 className="text-sm font-medium text-gray-200 capitalize">
            {type} Script
          </h3>
          <span className="text-xs text-gray-400 bg-gray-700 px-2 py-1 rounded font-mono">
            JavaScript
          </span>
        </div>
        
        <div className="flex items-center gap-2">
          {/* templates Dropdown */}
          <div className="relative group">
            <button className="flex items-center gap-1 px-3 py-1.5 text-xs bg-gray-700 hover:bg-gray-600 text-gray-300 border border-gray-600 rounded transition-colors cursor-pointer">
              <span>Templates</span>
              <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
              </svg>
            </button>
            
            <div className="absolute right-0 top-full mt-1 w-64 bg-gray-800 border border-gray-600 rounded-md shadow-xl opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200 z-10">
              <div className="p-2">
                <div className="text-xs font-medium text-gray-300 mb-2">Script Templates</div>
                {getScriptTemplates().map((template, index) => (
                  <button
                    key={index}
                    onClick={() => insertTemplate(template)}
                    className="w-full text-left px-2 py-1.5 text-xs text-gray-400 hover:bg-gray-700 hover:text-gray-200 rounded transition-colors"
                  >
                    {template.name}
                  </button>
                ))}
              </div>
            </div>
          </div>
          
          {/* action Buttons */}
          <button
            onClick={handleCopy}
            className="flex items-center gap-1 px-3 py-1.5 text-xs bg-gray-700 hover:bg-gray-600 text-gray-300 border border-gray-600 rounded transition-colors cursor-pointer"
          >
            <Copy size={12} />
          </button>
          
          <button
            onClick={handleDownload}
            className="flex items-center gap-1 px-3 py-1.5 text-xs bg-gray-700 hover:bg-gray-600 text-gray-300 border border-gray-600 rounded transition-colors cursor-pointer"
          >
            <Download size={12} />
          </button>
        </div>
      </div>

      {/* main Content - resizable */}
      <div className="flex-1 flex flex-col min-h-0">
        {/* editor section */}
        <div 
          className="flex-1 min-h-0 bg-gray-900"
          style={{ height: showConsole ? `calc(100% - ${consoleHeight}px)` : '100%' }}
        >
          <Editor
            key={`${type}-editor`}
            height="100%"
            language="javascript"
            value={localContent}
            onChange={handleContentChange}
            onMount={handleEditorDidMount}
            options={editorOptions}
            theme="vs-dark"
            loading={<div className="flex items-center justify-center h-full text-gray-400 bg-gray-900">Loading editor...</div>}
          />
        </div>
        
        {/* resizable drag handle */}
        {showConsole && (
          <div
            ref={dragRef}
            className={`h-1 bg-gray-700 hover:bg-gray-600 cursor-row-resize transition-colors ${
              isDragging ? 'bg-blue-500' : ''
            }`}
            onMouseDown={handleMouseDown}
          >
            <div className="h-full w-full flex items-center justify-center">
              <div className="w-8 h-0.5 bg-gray-500 rounded"></div>
            </div>
          </div>
        )}

        {/* resizable Console Section */}
        {showConsole && (
          <div
            className="border-t border-gray-700 flex flex-col bg-gray-900 shadow-[0_-4px_6px_-1px_rgba(0,0,0,0.1)]"
            style={{ height: `${consoleHeight}px` }}
          >
            {/* Console Header */}
            <div className="flex items-center justify-between px-3 py-1.5 bg-[#1e1e1e] border-b border-gray-700">
              <div className="flex items-center gap-3">
                <div className="flex items-center gap-1.5">
                  <span className="w-2 h-2 rounded-full bg-green-500 animate-pulse"></span>
                  <h4 className="text-xs font-semibold text-gray-300 uppercase tracking-wider font-apple">Console output</h4>
                </div>
                {logs.length > 0 && (
                  <span className="text-[10px] text-gray-400 bg-gray-800 border border-gray-700 px-1.5 py-0.5 rounded-full font-mono">
                    {logs.length} items
                  </span>
                )}
              </div>
              <div className="flex items-center gap-2">
                <button
                  onClick={onClearLogs}
                  className="text-xs text-gray-400 hover:text-white px-2 py-1 hover:bg-gray-700 rounded transition-colors"
                  title="Clear Console"
                >
                  Clear
                </button>
                {/* We rely on the parent's toggle for hiding, or we could add an X icon if we passed setShowConsole */}
              </div>
            </div>

            {/* Console Body */}
            <div className="flex-1 p-3 text-[13px] font-mono overflow-y-auto scrollbar-thin scrollbar-thumb-gray-600 scrollbar-track-gray-800">
              {logs.length === 0 ? (
                <div className="text-gray-500 flex items-center justify-center h-full">
                  <div className="text-center">
                    <svg className="w-8 h-8 mx-auto text-gray-600 mb-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M8 9l3 3-3 3m5 0h3M4 15V9a2 2 0 012-2h12a2 2 0 012 2v6a2 2 0 01-2 2H6a2 2 0 01-2-2z" />
                    </svg>
                    <div className="text-gray-400 font-apple">No output yet</div>
                    <div className="text-gray-500 text-xs mt-1 font-apple">Run your scripts to see logs here</div>
                  </div>
                </div>
              ) : (
                <div className="space-y-1">
                  {logs.map((log, index) => {
                    const isError = log.type === 'error';
                    const isWarn = log.type === 'warn';
                    const isInfo = log.type === 'info';
                    
                    return (
                      <div
                        key={index}
                        className={`flex items-start break-words py-1 border-b border-gray-800/50 last:border-0 hover:bg-gray-800/30 transition-colors ${
                          isError ? 'text-red-400 bg-red-900/10' :
                          isWarn ? 'text-yellow-400 bg-yellow-900/10' :
                          isInfo ? 'text-blue-400' :
                          'text-gray-300'
                        }`}
                      >
                        <span className={`shrink-0 w-16 text-[10px] mt-0.5 font-bold uppercase tracking-wider ${
                          isError ? 'text-red-500' :
                          isWarn ? 'text-yellow-500' :
                          isInfo ? 'text-blue-500' :
                          'text-gray-500'
                        }`}>
                          [{log.type}]
                        </span>
                        <span className="flex-1 ml-2 font-mono leading-relaxed whitespace-pre-wrap">
                          {log.args.join(' ')}
                        </span>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default ScriptEditor;
